import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';

// Analytics
import Analytics from './components/Analytics';

// Layouts
import AppLayout from './components/layout/AppLayout';
import AdminLayout from './components/layout/AdminLayout';

// Auth
import AuthGuard from './components/auth/AuthGuard';
import AdminGuard from './components/auth/AdminGuard';

// Public Pages
import LandingPage from './pages/LandingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import FeedbackPage from './pages/FeedbackPage';
import SubscribePage from './pages/SubscribePage';
import UpgradePage from './pages/UpgradePage';
import UpgradeSuccessPage from './pages/UpgradeSuccessPage';
import UpgradeCancelPage from './pages/UpgradeCancelPage';

// Auth Pages
import AuthPage from './pages/auth/AuthPage';
import VerifyPage from './pages/auth/VerifyPage';
import AdminLoginPage from './pages/auth/AdminLoginPage';

// Demo Pages
import DemoPage from './pages/demo/DemoPage';

// App Pages
import DashboardPage from './pages/app/DashboardPage';
import TransactionsPage from './pages/app/TransactionsPage';
import PotsPage from './pages/app/PotsPage';
import TagsPage from './pages/app/TagsPage';
import ReportsPage from './pages/app/ReportsPage';
import CategoriesPage from './pages/app/CategoriesPage';
import BudgetsPage from './pages/app/BudgetsPage';
import RecurringPage from './pages/app/RecurringPage';
import UserSettingsPage from './pages/app/SettingsPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ErrorsPage from './pages/admin/ErrorsPage';
import UsersListPage from './pages/admin/users/UsersListPage';
import UserDetailPage from './pages/admin/users/UserDetailPage';
import PlansListPage from './pages/admin/plans/PlansListPage';
import PlanDetailPage from './pages/admin/plans/PlanDetailPage';
import PlanCreatePage from './pages/admin/plans/PlanCreatePage';
import UsageOverviewPage from './pages/admin/usage/UsageOverviewPage';
import CostsConfigPage from './pages/admin/costs/CostsConfigPage';
import MarginAnalysisPage from './pages/admin/costs/MarginAnalysisPage';
import BillingOverviewPage from './pages/admin/billing/BillingOverviewPage';
import BillingEventsPage from './pages/admin/billing/BillingEventsPage';
import SettingsPage from './pages/admin/settings/SettingsPage';
import PromoCodesListPage from './pages/admin/promo-codes/PromoCodesListPage';
import {
  ReferralsDashboard,
  ReferralsListPage,
  ReferralUsersListPage,
  ReferralUserDetailPage,
} from './pages/admin/referrals';
import FeedbacksListPage from './pages/admin/feedbacks/FeedbacksListPage';

// Store
import { useAuthStore } from './stores/authStore';

function App() {
  const { initializeTheme } = useAuthStore();

  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  return (
    <>
      <Analytics />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/subscribe" element={<SubscribePage />} />
      <Route path="/upgrade/success" element={<UpgradeSuccessPage />} />
      <Route path="/upgrade/cancel" element={<UpgradeCancelPage />} />
      <Route path="/upgrade/:token" element={<UpgradePage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/demo" element={<DemoPage />} />

      {/* Protected User Routes */}
      <Route
        path="/app"
        element={
          <AuthGuard>
            <AppLayout />
          </AuthGuard>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="pots" element={<PotsPage />} />
        <Route path="tags" element={<TagsPage />} />
        <Route path="receipts" element={<Navigate to="/app/transactions" replace />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="budgets" element={<BudgetsPage />} />
        <Route path="recurring" element={<RecurringPage />} />
        <Route path="settings" element={<UserSettingsPage />} />
      </Route>

      {/* Protected Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<UsersListPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="plans" element={<PlansListPage />} />
        <Route path="plans/new" element={<PlanCreatePage />} />
        <Route path="plans/:id" element={<PlanDetailPage />} />
        <Route path="usage" element={<UsageOverviewPage />} />
        <Route path="costs" element={<CostsConfigPage />} />
        <Route path="costs/margin" element={<MarginAnalysisPage />} />
        <Route path="billing" element={<BillingOverviewPage />} />
        <Route path="billing/events" element={<BillingEventsPage />} />
        <Route path="errors" element={<ErrorsPage />} />
        <Route path="feedbacks" element={<FeedbacksListPage />} />
        <Route path="promo-codes" element={<PromoCodesListPage />} />
        <Route path="referrals" element={<ReferralsDashboard />} />
        <Route path="referrals/list" element={<ReferralsListPage />} />
        <Route path="referrals/users" element={<ReferralUsersListPage />} />
        <Route path="referrals/users/:userId" element={<ReferralUserDetailPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default App;
